import { useEffect, useState, Fragment, useCallback } from "react";
import { useAppSelector } from "../../../hooks/redux";
import { adminApi } from "../../../repositories/admin";
import { pitchesApi } from "../../../repositories/pitches";
import { Menu, Transition } from "@headlessui/react";

import {
  EllipsisVerticalIcon,
  UserPlusIcon,
  LightBulbIcon,
  ArrowUpIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import DashboardTableHeader from "./DashboardTableHeader";
import DashboardTableRow from "./DashboardTableRow";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { EyeIcon } from "lucide-react";
import Loader from "../../../components/common/Loader";
import { userApi } from "../../../repositories/user";
import { transactionApi } from "../../../repositories/transaction";
import { handleError } from "../../../utils/errorHandler";
import CSVUpload from "./CSVUpload";
import DeleteConfirmationModal from "../../../components/modal/DeleteConfirmationModal";

interface PurchaseData {
  id: number;
  email: string;
  productId: string;
  status: string;
  transactionId: string;
  pitchLimit: number;
  userId: number;
  packageName: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

interface Price {
  id: string;
  object: string;
  active: boolean;
  billing_scheme: string;
  created: number;
  currency: string;
  custom_unit_amount: number | null;
  livemode: boolean;
  lookup_key: string | null;
  metadata: { [key: string]: string };
  nickname: string | null;
  product: string;
  recurring: any | null;
  tax_behavior: string;
  tiers_mode: string | null;
  transform_quantity: any | null;
  type: string;
  unit_amount: number;
  unit_amount_decimal: string;
}

interface StripeProduct {
  id: string;
  name: string;
  description: string;
  active: boolean;
  metadata: {
    payment_link: string;
    pitch_limit: string;
    product_id: string;
  };
  prices: Price[];
}

interface ProfileData {
  purchase: PurchaseData | null;
}
interface Pitch {
  id: number;
  userId: number;
  companyName: string;
  artistName: string;
  releaseTitle: string;
  releaseDate: string;
  configuration: string;
  releaseUPC: string;
  trackISRC: string;
  status: string;
  deliveredDSPs?: string[];
  AmazonInfo?: any;
  SpotifyInfo?: any;
  DeezerInfo?: any;
  TidalInfo?: any;
  AudiomackInfo?: any;
}

export default function DashboardTable() {
  const [loading, setLoading] = useState(false);
  const { user } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [usersCurrentPage, setUsersCurrentPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [profileData, setProfileData] = useState<ProfileData>({
    purchase: null,
  });
  const [products, setProducts] = useState<StripeProduct[]>([]);
  const [selectedPitchIds, setSelectedPitchIds] = useState<number[]>([]);

  // Search and filter states for pitches
  const [pitchSearchTerm, setPitchSearchTerm] = useState("");
  const [pitchSearchType, setPitchSearchType] = useState<"artist" | "release" | "company">("artist");
  const [pitchStatusFilter, setPitchStatusFilter] = useState<string[]>([]);
  const [pitchFilterExpanded, setPitchFilterExpanded] = useState(false);

  // Search and filter states for users
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userSearchType, setUserSearchType] = useState<"name" | "email" | "company">("name");
  // CSV ulpoad
  const [showCSVUpload, setShowCSVUpload] = useState(false);
  const [deletePitchIds, setDeletePitchIds] = useState<number[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteInput, setDeleteInput] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const profileResponse = await userApi.getProfile();
        if (!profileResponse?.data?.success) {
          throw new Error(
            profileResponse?.data?.error || "Failed to fetch profile"
          );
        }
        setProfileData({
          purchase: profileResponse.data.data.purchase || null,
        });

        const productsResponse = await transactionApi.getAllStripeProducts();
        if (!productsResponse?.data?.success) {
          throw new Error(
            productsResponse?.data?.error || "Failed to fetch products"
          );
        }
        setProducts(productsResponse.data.data || []);
      } catch (error) {
        handleError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);
  const [modalLoading, setModalLoading] = useState(false);

  const handleDeleteConfirmed = async () => {
    setModalLoading(true);
    try {
      const response = await pitchesApi.deletePitch(selectedPitchIds);
      setSelectedPitchIds([]);
      setShowDeleteModal(false);
      setDeleteInput("");
      await fetchData(currentPage, usersCurrentPage);

      Swal.fire({
        title: response?.data?.success ? "Deleted!" : "Error!",
        text: response?.data?.message || "Pitch deleted successfully!",
        icon: response?.data?.success ? "success" : "error",
        confirmButtonText: "OK",
      });
    } catch (error: any) {
      setShowDeleteModal(false);
      setDeleteInput("");
      Swal.fire({
        title: "Error!",
        text: error?.response?.data?.message || error.message || "Request failed",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setModalLoading(false);
    }
  };


  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!loading) {
        fetchData(1, 1);
        setCurrentPage(1);
        setUsersCurrentPage(1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [pitchSearchTerm, pitchSearchType, userSearchTerm, userSearchType, pitchStatusFilter]);

  const fetchData = useCallback(async (
    pitchPage: number = currentPage,
    userPage: number = usersCurrentPage
  ) => {
    try {
      setLoading(true);
      if (user?.role === "admin") {
        const [usersResponse, pitchesResponse] = await Promise.all([
          adminApi.getUsers({
            page: userPage,
            limit: 10,
            search: userSearchTerm,
            searchType: userSearchType
          }),
          pitchesApi.getAll({
            page: pitchPage,
            limit: 10,
            search: pitchSearchTerm,
            searchType: pitchSearchType,
            status: pitchStatusFilter
          }),
        ]);
        setUsers(usersResponse.data.data || []);
        setFilteredUsers(usersResponse.data.data || []);
        setData(pitchesResponse.data.data || []);
        setFilteredData(pitchesResponse.data.data || []);
        setTotalPages(pitchesResponse.data.pagination.totalPages);
        setUsersTotalPages(usersResponse.data.pagination.totalPages);
      } else {
        const pitchesResponse = await pitchesApi.getAll({
          page: pitchPage,
          limit: 10,
          search: pitchSearchTerm,
          searchType: pitchSearchType,
          status: pitchStatusFilter,
        });
        setData(pitchesResponse.data.data || []);
        setFilteredData(pitchesResponse.data.data || []);
        setTotalPages(pitchesResponse.data.pagination.totalPages);
      }
    } catch (error) {
      setUsers([]);
      setFilteredUsers([]);
      setData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [pitchSearchTerm, userSearchTerm, pitchSearchType, userSearchType, pitchStatusFilter, user?.role]);



  const promote = async (userId: number) => {
    try {
      await adminApi.promoteUser(userId);
      Swal.fire({
        title: "Success!",
        text: "User Promoted Successfully!",
        icon: "success",
      });
      await fetchData(currentPage, usersCurrentPage);
    } catch (error: any) {
      Swal.fire({
        title: "Error!",
        text: error.response?.data?.message || "Request failed",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const deleteUser = async (userId: number) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        await adminApi.deleteUser(userId);
        Swal.fire({
          title: "Deleted!",
          text: "User Deleted Successfully!",
          icon: "success",
        });
        await fetchData(currentPage, usersCurrentPage);
      }
    } catch (error: any) {
      Swal.fire({
        title: "Error!",
        text:
          error?.response?.data?.message || error.message || "Request failed",
        icon: "error",
        confirmButtonText: "OK",
      });
      console.error(error);
    }
  };


  const handlePitchPageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchData(newPage, usersCurrentPage);
    }
  };

  const handleUserPageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= usersTotalPages) {
      setUsersCurrentPage(newPage);
      fetchData(currentPage, newPage);
    }
  };

  const handlePitchStatusFilterChange = (status: string) => {
    setPitchStatusFilter(prev =>
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  // const clearUserFilters = () => {
  //   setUserSearchTerm("");
  // };

  const clearPitchFilters = () => {
    setPitchSearchTerm("");

  };

  // Clear only status filters (for the "Clear status filters" button)
  const clearPitchStatusFilters = () => {
    setPitchStatusFilter([]);
  };


  const handleRowCheck = (id: number, checked: boolean) => {
    setSelectedPitchIds(prev =>
      checked ? [...prev, id] : prev.filter(pid => pid !== id)
    );
  };

  // Select all logic
  const allChecked = filteredData.length > 0 && filteredData.every((row: Pitch) => selectedPitchIds.includes(row.id));

  const handleCheckAll = (checked: boolean) => {
    const pageIds = filteredData.map((row: Pitch) => row.id);
    if (checked) {
      // Add current page IDs to selectedPitchIds (avoid duplicates)
      setSelectedPitchIds(prev =>
        Array.from(new Set([...prev, ...pageIds]))
      );
    } else {
      // Remove only current page IDs from selectedPitchIds
      setSelectedPitchIds(prev =>
        prev.filter(id => !pageIds.includes(id))
      );
    }
  };


  if (loading) return <Loader />;

  return (
    <div className="min-h-screen p-4 sm:p-6">
      <div className="flex flex-col items-start justify-between gap-4 mb-6 md:flex-row md:items-center">
        <h2 className="text-xl font-semibold text-gray-800 sm:text-2xl">
          {user?.role === "basic" ? "User" : "Admin"} Dashboard
        </h2>
        <div className="flex flex-col w-full gap-3 sm:flex-row sm:w-auto">
          {user?.role === "admin" && (
            <button
              onClick={() => navigate("/admin/invite")}
              className="flex items-center justify-center w-full gap-2 px-4 py-2 text-sm text-white bg-blue-600 rounded-lg sm:text-base sm:w-auto hover:bg-blue-700"
            >
              <UserPlusIcon className="w-5 h-5" /> Invite User
            </button>
          )}
          <button
            onClick={() => setShowCSVUpload(true)}
            className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md border-2x-4 hover:bg-gray-700">
            Upload Release Schedule
          </button>
          <button
            onClick={() => navigate("/pitch/create")}
            className="flex items-center justify-center w-full gap-2 px-4 py-2 text-sm text-white bg-green-600 rounded-lg sm:text-base sm:w-auto hover:bg-green-700"
          >
            <LightBulbIcon className="w-5 h-5" /> Create Pitch
          </button>
        </div>
      </div>


      {/* Pitches Section Header with Search */}
      <div className="flex flex-col p-4 bg-gray-200 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="mb-4 text-xl font-semibold sm:mb-0">Pitches</h3>
        {
          selectedPitchIds.length > 0 && (
            <div className="flex items-center space-x-4 w-full pl-4">
              <span className="text-sm text-gray-700">
                {selectedPitchIds.length} selected
              </span>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="flex items-center gap-2 px-4 py-2 border border-red-300 rounded-md  hover:bg-red-50 focus:ring-2 focus:ring-red-200 transition"
                aria-label="Delete selected pitches"
              >
                <TrashIcon className="w-5 h-5 text-red-600" />
                <span className="text-sm text-red-600 font-semibold">Delete Selected Pitches</span>
              </button>
            </div>
          )
        }



        {/* Pitches Search - Replacing remaining pitches area */}
        <div className="flex flex-col gap-3 sm:flex-row">
          {/* Search Type Selector */}
          <div className="relative">
            <select
              value={pitchSearchType}
              onChange={(e) => setPitchSearchType(e.target.value as "artist" | "release" | "company")}
              className="px-3 py-2 pr-8 text-sm bg-white border border-gray-300 appearance-none rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="artist">Artist Name</option>
              <option value="release">Release Name</option>
              <option value="company">Company Name</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <ChevronDownIcon className="w-4 h-4 text-gray-400" />
            </div>
          </div>

          {/* Search Input */}
          <div className="relative flex-1 sm:w-64">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={pitchSearchTerm}
              onChange={(e) => setPitchSearchTerm(e.target.value)}
              placeholder={`Search by ${pitchSearchType === "artist" ? "artist name" : pitchSearchType === "release" ? "release name" : "company name"}...`}
              className="block w-full py-2 pl-10 pr-10 text-sm placeholder-gray-500 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            {pitchSearchTerm && (
              <button
                onClick={() => setPitchSearchTerm("")}
                className="absolute inset-y-0 right-0 flex items-center pr-3"
              >
                <XMarkIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>

          {/* Clear Filters Button */}
          {(pitchSearchTerm || pitchStatusFilter.length > 0) && (
            <button
              onClick={clearPitchFilters}
              className="px-3 py-2 text-sm text-blue-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Pitches Filter Section */}
      <div className="flex flex-col items-start bg-white border border-gray-200 rounded-t-lg sm:flex-row sm:items-start">
        <div className="w-full sm:w-[70%]">
          <button
            onClick={() => setPitchFilterExpanded(!pitchFilterExpanded)}
            className="flex items-center justify-between w-full p-3 text-left hover:bg-gray-50"
          >
            <div className="flex items-center">
              <FunnelIcon className="w-4 h-4 mr-2 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Status Filter</span>
              {pitchStatusFilter.length > 0 && (
                <span className="px-2 py-1 ml-2 text-xs text-blue-800 bg-blue-100 rounded-full">
                  {pitchStatusFilter.length}
                </span>
              )}
            </div>
            {pitchFilterExpanded ? (
              <ChevronUpIcon className="w-4 h-4 text-gray-500" />
            ) : (
              <ChevronDownIcon className="w-4 h-4 text-gray-500" />
            )}
          </button>
          {pitchFilterExpanded && (
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <div className="flex flex-wrap gap-2 mb-3">
                {["draft", "submitted", "delivered"].map((status) => (
                  <label key={status} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={pitchStatusFilter.includes(status)}
                      onChange={() => handlePitchStatusFilterChange(status)}
                      className="mr-2 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700 capitalize">{status}</span>
                  </label>
                ))}
              </div>
              {pitchStatusFilter.length > 0 && (
                <button
                  onClick={clearPitchStatusFilters}
                  className="text-sm text-blue-600 underline hover:text-blue-800"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </div>
        <div className={`w-full sm:w-[30%] flex items-center justify-center border-t sm:border-t-0 sm:border-l ${pitchFilterExpanded ? "hidden sm:flex" : "flex"}`}>
          <div className="px-4 py-3 text-center">
            <p className="text-sm font-medium text-gray-700">
              <span className="font-semibold text-blue-600">
                {profileData.purchase?.pitchLimit ?? 0}
              </span>{" "}
              Remaining Pitches
            </p>
          </div>
        </div>



      </div>

      {/* Pitches Table */}
      <div className="overflow-x-auto bg-white border border-gray-200 rounded-b-lg">
        <table className="min-w-full bg-white border-collapse rounded-lg whitespace-nowrap">
          <DashboardTableHeader
            allChecked={allChecked}
            onCheckAll={handleCheckAll}
          />
          <tbody>
            {filteredData.length > 0 ? (
              filteredData.map((row: Pitch, index) => (
                <DashboardTableRow
                  key={Number(row.id)}
                  data={row}
                  onStatusChange={() => fetchData(currentPage, usersCurrentPage)}
                  checked={selectedPitchIds.includes(row.id)}
                  onCheck={handleRowCheck}
                />
              ))
            ) : (
              <tr>
                <td className="p-4 text-center text-gray-500 min-h-[200px]" colSpan={7}>
                  {pitchSearchTerm || pitchStatusFilter.length > 0 ? "No pitches found matching your search criteria." : "No data found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col items-center justify-between gap-4 mt-6 sm:flex-row">
        <button
          disabled={currentPage === 1}
          onClick={() => handlePitchPageChange(currentPage - 1)}
          className="px-4 py-2 bg-gray-200 rounded-md disabled:opacity-50"
        >
          Previous
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => handlePitchPageChange(currentPage + 1)}
          className="px-4 py-2 bg-gray-200 rounded-md disabled:opacity-50"
        >
          Next
        </button>
      </div>

      {user?.role === "admin" && (
        <div className="mt-4">
          {/* Users Section Header with Search */}
          <div className="flex flex-col p-4 bg-gray-200 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="mb-4 text-xl font-semibold sm:mb-0">Users</h3>

            {/* Users Search */}
            <div className="flex flex-col gap-3 sm:flex-row">
              {/* Search Type Selector */}
              <div className="relative">
                <select
                  value={userSearchType}
                  onChange={(e) => setUserSearchType(e.target.value as "name" | "email" | "company")}
                  className="px-3 py-2 pr-8 text-sm bg-white border border-gray-300 appearance-none rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="name">User Name</option>
                  <option value="email">Email</option>
                  <option value="company">Company</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <ChevronDownIcon className="w-4 h-4 text-gray-400" />
                </div>
              </div>

              {/* Search Input */}
              <div className="relative flex-1 sm:w-64">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={userSearchTerm}
                  onChange={(e) => setUserSearchTerm(e.target.value)}
                  placeholder={`Search by ${userSearchType === "name" ? "user name" : userSearchType === "email" ? "email" : "company"}...`}
                  className="block w-full py-2 pl-10 pr-10 text-sm placeholder-gray-500 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {userSearchTerm && (
                  <button
                    onClick={() => setUserSearchTerm("")}
                    // onClick={clearUserFilters} 
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                  >
                    <XMarkIcon className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>

              {/* Clear Filters Button */}
              {/* {userSearchTerm && (
                <button
                  // onClick={clearUserFilters}
                    onClick={() => setUserSearchTerm("")}
                  className="px-3 py-2 text-sm text-blue-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Clear Filters
                </button>
              )} */}
            </div>
          </div>



          {/* Users Table */}
          <div className="overflow-x-auto bg-white border border-gray-200 rounded-b-lg">
            <table className="min-w-full bg-white border-collapse rounded-lg whitespace-nowrap">
              <thead>
                <tr className="text-left text-gray-700 bg-gray-100 border-b">
                  <th className="p-4">ID</th>
                  <th className="p-4">User Name</th>
                  <th className="p-4">Email</th>
                  <th className="p-4">Company</th>
                  <th className="p-4">Role</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((userItem: any, index: number) => (
                    <tr
                      key={index}
                      className="transition border-b hover:bg-gray-100"
                    >
                      <td className="p-4">{userItem?.id ?? "N/A"}</td>
                      <td className="p-4">{userItem?.fullName ?? "N/A"}</td>
                      <td className="p-4">{userItem?.email ?? "N/A"}</td>
                      <td className="p-4">{userItem?.Label?.name ?? "N/A"}</td>
                      <td className="p-4 capitalize">
                        {userItem?.role ?? "N/A"}
                      </td>
                      <td className="p-4 text-right">
                        <Menu
                          as="div"
                          className="relative inline-block text-left"
                        >
                          <Menu.Button className="p-2 rounded-md hover:bg-gray-200">
                            <EllipsisVerticalIcon className="w-5 h-5" />
                          </Menu.Button>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                          >
                            <Menu.Items className="absolute right-0 z-10 w-48 bg-white divide-y divide-gray-100 rounded-md shadow-lg -top-5">
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() =>
                                      navigate(`/admin/userdsp/${userItem.id}`)
                                    }
                                    className={`${active ? "bg-gray-100" : ""
                                      } group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                                  >
                                    <EyeIcon className="w-5 h-5 mr-2 text-gray-500" />
                                    View User
                                  </button>
                                )}
                              </Menu.Item>
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => promote(userItem.id)}
                                    className={`${active ? "bg-gray-100" : ""
                                      } group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                                  >
                                    <ArrowUpIcon className="w-5 h-5 mr-2 text-gray-500" />
                                    Promote User
                                  </button>
                                )}
                              </Menu.Item>
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={() => deleteUser(userItem.id)}
                                    className={`${active ? "bg-gray-100" : ""
                                      } group flex w-full items-center px-4 py-2 text-sm text-red-600`}
                                  >
                                    <TrashIcon className="w-5 h-5 mr-2 text-red-600" />
                                    Remove User
                                  </button>
                                )}
                              </Menu.Item>
                            </Menu.Items>
                          </Transition>
                        </Menu>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="p-4 text-center text-gray-500">
                      {userSearchTerm ? "No users found matching your search criteria." : "No users found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col items-center justify-between gap-4 mt-4 sm:flex-row">
            <button
              disabled={usersCurrentPage === 1}
              onClick={() => handleUserPageChange(usersCurrentPage - 1)}
              className="px-4 py-2 bg-gray-200 rounded-md disabled:opacity-50"
            >
              Previous
            </button>
            <span>
              Page {usersCurrentPage} of {usersTotalPages}
            </span>
            <button
              disabled={usersCurrentPage === usersTotalPages}
              onClick={() => handleUserPageChange(usersCurrentPage + 1)}
              className="px-4 py-2 bg-gray-200 rounded-md disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
      {showCSVUpload && <div>
        <CSVUpload onClose={() => setShowCSVUpload(false)} onSuccess={() => true} />
      </div>}
      <DeleteConfirmationModal
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirmed}
        inputValue={deleteInput}
        setInputValue={setDeleteInput}
        loading={modalLoading}
      />
    </div>
  );
}
